import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddLedgerEntry from 'components/add-entry-modal/add-entry-modal';
import { createAddEntrySchema } from 'components/add-entry-modal/services/validate-entry';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountingType } from 'v2-types/order';
import { SplitPayeePayer } from 'views/orders/new-charges/components/split-payee/split-payee';

import Header from '../components/header';
import { columnsDataSet } from '../components/table-charges-kmln/services';
import TablePrototypeKdues from '../components/table-charges-kmln/table-prototype-kdues';
import { chargesSorted, getDisabledFieldsv2, getHideFieldsv2 } from '../services';
import { mockData } from './services';

const addEntrySchema = createAddEntrySchema({ requireLetter: true });

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  marginCard: { marginBottom: theme.spacing(2) },
}));

type ChargesJKProps = {
  accountingData: AccountingType[];
  handleEditModal: (item: columnsDataSet) => void;
  handleDelete: (item: columnsDataSet) => void;
  openAddModal: boolean;
  handleaddModal: (modal: string | null) => void;
  state: { type: string; data?: any };
  showHeaderAndContainer: boolean;
  reloadAccounting: () => void;
};

const ChargesJKScene: FC<ChargesJKProps> = ({
  accountingData,
  handleEditModal,
  handleDelete,
  openAddModal,
  handleaddModal,
  state,
  showHeaderAndContainer,
  reloadAccounting,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [partySelection, setPartySelection] = useState<{
    kind: 'Payee' | 'Payer';
    charge: AccountingType;
  } | null>(null);
  const [itemsCollapsed, setItemsCollapsed] = useState<{ [key: string]: boolean }>({});

  const mapedData = useMemo(() => mockData(chargesSorted(accountingData, true)), [accountingData]);

  const handleToggleCollapse = (item: columnsDataSet) =>
    setItemsCollapsed((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  const handleChangeParties = (item: columnsDataSet, kind: 'Payee' | 'Payer') =>
    setPartySelection({ kind, charge: item.chargeData });
  const handleChangePayees = (item: columnsDataSet) => handleChangeParties(item, 'Payee');
  const handleChangePayers = (item: columnsDataSet) => handleChangeParties(item, 'Payer');

  const paperContent = (
    <Grid container direction="row">
      <Grid item xs={12} md={6}>
        <Box display="flex" flexDirection="column">
          <TablePrototypeKdues
            data={mapedData.grossAmountBorrower}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:gross-amount-due')}
            type="dues"
          />
          <TablePrototypeKdues
            data={[
              ...mapedData.JAdjustmentsItems,
              ...mapedData.JGrosstotals,
              ...mapedData.JAdjustmentsItemssecond,
            ]}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items')}
            type="adjustments"
          />
          <TablePrototypeKdues
            data={[
              ...mapedData.JUnpaidItems,
              ...mapedData.JUnpaidTotals,
              ...mapedData.CashBorrower,
            ]}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items-updaid')}
            type="adjustments"
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box display="flex" flexDirection="column">
          <TablePrototypeKdues
            data={mapedData.grossAmountSeller}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:gross-due-seller')}
            type="dues"
          />
          <TablePrototypeKdues
            data={[
              ...mapedData.KAdjustments,
              ...mapedData.KGrosstotals,
              ...mapedData.KAdjustmentssecond,
            ]}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items')}
            type="adjustments"
          />
          <TablePrototypeKdues
            data={[...mapedData.KUnpaidItems, ...mapedData.KUnpaidTotals, ...mapedData.CaskSeller]}
            handleEditModal={handleEditModal}
            handleDelete={handleDelete}
            handleChangePayees={handleChangePayees}
            handleChangePayers={handleChangePayers}
            itemsCollapsed={itemsCollapsed}
            onToggleCollapse={handleToggleCollapse}
            title={t('charges:adjustments-for-items-updaid')}
            type="adjustments"
          />
        </Box>
      </Grid>
    </Grid>
  );

  const modals = (
    <>
      {openAddModal && (
        <AddLedgerEntry
          open={!!openAddModal}
          handleClose={() => {
            handleaddModal(null);
          }}
          prevData={state.data!}
          lettersAllowedEntry={['j', 'k']}
          kindAllowedEntry={['manual']}
          codeAllowedEntry={['charge']}
          defaultCode="charge"
          defaultKind="manual"
          resolver={zodResolver(addEntrySchema)}
          disableFields={getDisabledFieldsv2(state)}
          hideFields={getHideFieldsv2(state)}
          isEditingEntry={state.type === 'edit'}
        />
      )}
      {partySelection && (
        <SplitPayeePayer
          open
          kind={partySelection.kind}
          accountingEntry={partySelection.charge}
          onClose={() => setPartySelection(null)}
          onSuccess={() => {
            setPartySelection(null);
            reloadAccounting();
          }}
        />
      )}
    </>
  );

  if (!showHeaderAndContainer) {
    return (
      <Grid container direction="column">
        {paperContent}
        {modals}
      </Grid>
    );
  }
  return (
    <>
      <Grid container direction="column" className={classes.padding}>
        <Grid item xs={12}>
          <Paper>
            <Header title={t('charges:summary-jk')} chargesIdentifier="J-K" />
            {paperContent}
          </Paper>
        </Grid>
      </Grid>
      {modals}
    </>
  );
};

export default ChargesJKScene;
